import { request } from "../core/request";
import { OPERATION_IDS, DEFAULT_CONNECTION_FROM } from "../constants";
import type { ITwfInsertApiRequest } from "../types/twfInsertTypes";
import type { IUcfInsertApiRequest } from "../types/ucfInsertTypes";
import type { IPlcsInsertApiRequest, IPlcsInsertApiResponse } from "../types/plcsInsertApiTypes";
import { plcsInsert } from "./plcsInsert.service";

/**
 * Home Loan Insert Request
 */
export interface HomeLoanInsertRequest {
  mobileNo: string;
  loanAmount: number;
  tenure: number;
  propertyValue?: number;
  pincode?: string;
  employmentType?: string;
  [key: string]: unknown; // Allow additional fields
}

/**
 * Business Loan Insert Request
 */
export interface BusinessLoanInsertRequest {
  mobileNo: string;
  loanAmount: number;
  tenure: number;
  businessType?: string;
  businessVintage?: number;
  [key: string]: unknown;
}

/**
 * Gold Loan Insert Request
 */
export interface GoldLoanInsertRequest {
  mobileNo: string;
  loanAmount: number;
  goldWeight?: number;
  goldPurity?: string;
  [key: string]: unknown;
}

/**
 * Personal Loan Insert Request
 */
export interface PersonalLoanInsertRequest {
  mobileNo: string;
  loanAmount: number;
  tenure: number;
  employmentType?: string;
  monthlyIncome?: number;
  [key: string]: unknown;
}

/**
 * LAP (Loan Against Property) Insert Request
 */
export interface LAPInsertRequest {
  mobileNo: string;
  loanAmount: number;
  tenure: number;
  propertyType?: string;
  propertyValue?: number;
  [key: string]: unknown;
}

/**
 * Generic Loan Response
 */
export interface LoanInsertResponse {
  statusCode: number;
  message: string;
  data?: {
    applicationId?: string;
    leadId?: string;
    [key: string]: unknown;
  };
}

/**
 * Insert Home Loan application
 *
 * @param data - Home loan application data
 * @returns Promise with application response
 *
 * @example
 * ```typescript
 * const result = await insertHomeLoan({
 *   mobileNo: '9876543210',
 *   loanAmount: 5000000,
 *   tenure: 240,
 *   propertyValue: 7000000
 * });
 * ```
 */
export async function insertHomeLoan(data: HomeLoanInsertRequest): Promise<LoanInsertResponse> {
  return request<LoanInsertResponse>({
    operationId: OPERATION_IDS.hlInsert,
    requestData: data,
    connectionFrom: DEFAULT_CONNECTION_FROM,
  });
}

/**
 * Insert Business Loan application
 */
export async function insertBusinessLoan(
  data: BusinessLoanInsertRequest
): Promise<LoanInsertResponse> {
  return request<LoanInsertResponse>({
    operationId: OPERATION_IDS.blInsert,
    requestData: data,
    connectionFrom: DEFAULT_CONNECTION_FROM,
  });
}

/**
 * Insert Gold Loan application
 */
export async function insertGoldLoan(data: GoldLoanInsertRequest): Promise<LoanInsertResponse> {
  return request<LoanInsertResponse>({
    operationId: OPERATION_IDS.glInsert,
    requestData: data,
    connectionFrom: DEFAULT_CONNECTION_FROM,
  });
}

/**
 * Insert Personal Loan application (PLCS)
 */
export async function insertPersonalLoan(
  data: IPlcsInsertApiRequest
): Promise<IPlcsInsertApiResponse> {
  return plcsInsert(data);
}

/**
 * Insert LAP (Loan Against Property) application
 */
export async function insertLAP(data: LAPInsertRequest): Promise<LoanInsertResponse> {
  return request<LoanInsertResponse>({
    operationId: OPERATION_IDS.lapInsert,
    requestData: data,
    connectionFrom: DEFAULT_CONNECTION_FROM,
  });
}

/**
 * Insert Two Wheeler Loan application
 */
export async function insertTwoWheelerLoan(
  data: ITwfInsertApiRequest
): Promise<LoanInsertResponse> {
  return request<LoanInsertResponse>({
    operationId: OPERATION_IDS.twfInsert,
    requestData: data,
    connectionFrom: DEFAULT_CONNECTION_FROM,
  });
}

/**
 * Insert Used Car Loan application
 */
export async function insertUsedCarLoan(data: IUcfInsertApiRequest): Promise<LoanInsertResponse> {
  return request<LoanInsertResponse>({
    operationId: OPERATION_IDS.ucfInsert,
    requestData: data,
    connectionFrom: DEFAULT_CONNECTION_FROM,
  });
}

/**
 * Get Gold Rate
 */
export async function getGoldRate(): Promise<unknown> {
  return request({
    operationId: OPERATION_IDS.goldRate,
    requestData: {},
    connectionFrom: DEFAULT_CONNECTION_FROM,
  });
}

/**
 * Get Loan Details
 */
export async function getLoanDetails(mobileNo: string): Promise<unknown> {
  return request({
    operationId: OPERATION_IDS.loanDetails,
    requestData: { mobileNo },
    connectionFrom: DEFAULT_CONNECTION_FROM,
  });
}
